/**
 * Financial-Grade Runtime Decimal Contract (Task 001a).
 * Project: Market-Hub
 * Governance Anchor: ASOP-TASK-TSO-V2-MARKET-HUB-CONTRACT-AND-ISOLATION-239A
 * Date: 2026-09-14
 * 
 * Invariants:
 * 1. Zero Binary Float AST: Domain state, math, models, and stores strictly prohibit binary numbers.
 * 2. Pre-coercion Rejection: Rejects boolean, number, NaN, Infinity, and scientific notation (e.g. 1e5).
 * 3. Presentation Leaf-Node Quarantine: Conversion to number is strictly quarantined to chart viewport edges.
 */

declare const DecimalBrand: unique symbol;

/**
 * Nominal type branding for pure decimal strings.
 * Cannot be accidentally constructed from unverified strings or numbers.
 */
export type DecimalString = string & { readonly [DecimalBrand]: true };

const STRICT_DECIMAL_PATTERN = /^-?(0|[1-9]\d*)(\.\d+)?$/;

/**
 * Runtime decimal string validator enforcing financial invariants.
 * Throws TypeError or RangeError on invalid input.
 */
export function parseDecimalString(raw: unknown): DecimalString {
    if (raw === null || raw === undefined) {
        throw new TypeError("Decimal value cannot be null or undefined");
    }

    if (typeof raw === "boolean") {
        throw new TypeError(`Forbidden boolean input for decimal value: ${raw}`);
    }

    if (typeof raw === "number") {
        throw new TypeError(
            `Forbidden binary float input: ${raw}. Financial values must be transmitted as exact decimal strings.`
        );
    }

    if (typeof raw !== "string") {
        throw new TypeError(`Invalid type for decimal value: expected string, got ${typeof raw}`);
    }

    const trimmed = raw.trim();
    if (trimmed.length === 0) {
        throw new RangeError("Decimal string cannot be empty or whitespace-only");
    }

    const lower = trimmed.toLowerCase();
    if (lower === "nan" || lower.includes("inf")) {
        throw new RangeError(`Forbidden non-finite value: ${trimmed}`);
    }

    if (lower.includes("e")) {
        throw new RangeError(`Scientific notation forbidden in financial decimal: ${trimmed}`);
    }

    if (!STRICT_DECIMAL_PATTERN.test(trimmed)) {
        throw new RangeError(`Invalid decimal format: '${trimmed}'`);
    }

    return trimmed as DecimalString;
}

/**
 * Type-guard predicate for DecimalString.
 */
export function isDecimalString(raw: unknown): raw is DecimalString {
    try {
        parseDecimalString(raw);
        return true;
    } catch {
        return false;
    }
}

/**
 * Presentation Leaf-Node Quarantine Adapter.
 * Converts DecimalString to number strictly for Canvas/SVG chart rendering coordinates.
 * 
 * WARNING: PROHIBITED IN DOMAIN, STORE, ARITHMETIC, OR WIRE SERIALIZATION LAYERS.
 */
export function toChartFloat(val: DecimalString): number {
    const num = Number(val);
    if (!Number.isFinite(num)) {
        throw new RangeError(`Failed to convert DecimalString to finite chart rendering float: ${val}`);
    }
    return num;
}

/**
 * Pure string-based decimal comparator.
 * Returns -1 if a < b, 0 if a === b, 1 if a > b.
 * Gracefully sorts null/undefined/empty values to the end.
 * Guarantees zero binary floating-point operations.
 */
export function compareDecimalStrings(rawA: unknown, rawB: unknown): number {
    const isNullA = rawA === null || rawA === undefined || rawA === '' || rawA === '--';
    const isNullB = rawB === null || rawB === undefined || rawB === '' || rawB === '--';

    if (isNullA && isNullB) return 0;
    if (isNullA) return 1;
    if (isNullB) return -1;

    const cleanA = String(rawA).replace(/,/g, '').trim();
    const cleanB = String(rawB).replace(/,/g, '').trim();

    if (cleanA === cleanB) return 0;

    const negA = cleanA.startsWith('-');
    const negB = cleanB.startsWith('-');

    if (negA && !negB) return -1;
    if (!negA && negB) return 1;

    const coreA = negA ? cleanA.slice(1) : cleanA;
    const coreB = negB ? cleanB.slice(1) : cleanB;

    const [rawIntA = '0', rawFracA = ''] = coreA.split('.');
    const [rawIntB = '0', rawFracB = ''] = coreB.split('.');

    const intA = rawIntA.replace(/^0+/, '') || '0';
    const intB = rawIntB.replace(/^0+/, '') || '0';

    if (intA.length !== intB.length) {
        const cmp = intA.length > intB.length ? 1 : -1;
        return negA ? -cmp : cmp;
    }

    if (intA !== intB) {
        const cmp = intA > intB ? 1 : -1;
        return negA ? -cmp : cmp;
    }

    const maxFracLen = Math.max(rawFracA.length, rawFracB.length);
    const fracA = rawFracA.padEnd(maxFracLen, '0');
    const fracB = rawFracB.padEnd(maxFracLen, '0');

    if (fracA !== fracB) {
        const cmp = fracA > fracB ? 1 : -1;
        return negA ? -cmp : cmp;
    }

    return 0;
}

/**
 * Returns the sign of a decimal string: -1 for negative, 0 for zero/null, 1 for positive.
 * Never converts to binary float.
 */
export function getDecimalSign(raw: unknown): -1 | 0 | 1 {
    if (raw === null || raw === undefined || raw === '' || raw === '--') return 0;
    const s = String(raw).replace(/,/g, '').trim();
    if (!s || s === '0' || s === '-0' || /^0+(\.0+)?$/.test(s) || /^-0+(\.0+)?$/.test(s)) {
        return 0;
    }
    return s.startsWith('-') ? -1 : 1;
}

export function isDecimalPositive(raw: unknown): boolean {
    return getDecimalSign(raw) === 1;
}

export function isDecimalNegative(raw: unknown): boolean {
    return getDecimalSign(raw) === -1;
}

export function isDecimalZero(raw: unknown): boolean {
    return getDecimalSign(raw) === 0;
}

/**
 * Calculates ((close - open) / open) * 100 with pure BigInt decimal precision.
 * Returns exact signed decimal string (e.g. "2.35" or "-1.20") or null if invalid/zero open.
 */
export function calculateDecimalPercentageChange(openRaw: unknown, closeRaw: unknown): string | null {
    if (openRaw === null || openRaw === undefined || closeRaw === null || closeRaw === undefined) {
        return null;
    }

    const strOpen = String(openRaw).replace(/,/g, '').trim();
    const strClose = String(closeRaw).replace(/,/g, '').trim();

    if (!strOpen || !strClose || strOpen === '--' || strClose === '--') {
        return null;
    }

    if (isDecimalZero(strOpen)) {
        return null;
    }

    const [openInt = '0', openFrac = ''] = strOpen.split('.');
    const [closeInt = '0', closeFrac = ''] = strClose.split('.');

    const scale = Math.max(openFrac.length, closeFrac.length, 6);
    const scaleFactor = 10n ** BigInt(scale);

    const openBig = BigInt(openInt) * scaleFactor + BigInt(openFrac.padEnd(scale, '0'));
    const closeBig = BigInt(closeInt) * scaleFactor + BigInt(closeFrac.padEnd(scale, '0'));

    if (openBig === 0n) return null;

    const diffBig = closeBig - openBig;
    // Scale by 100 (for percentage) and 100 (for 2 fraction digits) = 10000n
    const pctScaled = (diffBig * 10000n) / openBig;

    const isNeg = pctScaled < 0n;
    const absVal = isNeg ? -pctScaled : pctScaled;
    const intPart = absVal / 100n;
    const fracPart = absVal % 100n;

    const fracStr = fracPart.toString().padStart(2, '0');
    return `${isNeg ? '-' : ''}${intPart}.${fracStr}`;
}

/**
 * Formats a decimal ratio string (e.g. "0.6500", "1.0000", "0.0520") as an exact percentage string (e.g. "65.0%", "100.0%", "5.2%").
 * Performs exact BigInt arithmetic with zero binary float operations.
 */
export function formatDecimalPercent(rawVal: unknown, decimals: number = 1): string {
    if (rawVal === null || rawVal === undefined || rawVal === '' || rawVal === '--') {
        return '—';
    }
    const str = String(rawVal).replace(/,/g, '').trim();
    if (!str || str === '--') return '—';

    const isNeg = str.startsWith('-');
    const clean = isNeg ? str.slice(1) : str;
    const [rawInt = '0', rawFrac = ''] = clean.split('.');

    const totalScale = Math.max(rawFrac.length, 6);
    const factor = 10n ** BigInt(totalScale);
    const valueBig = BigInt(rawInt) * factor + BigInt(rawFrac.padEnd(totalScale, '0'));

    const targetScale = BigInt(decimals);
    const mult = 100n * (10n ** targetScale);
    const scaled = (valueBig * mult + (factor / 2n)) / factor;
    const divisor = 10n ** targetScale;
    const outInt = scaled / divisor;
    const outFrac = scaled % divisor;

    const fracStr = decimals > 0 ? `.${outFrac.toString().padStart(decimals, '0')}` : '';
    return `${isNeg && (outInt > 0n || outFrac > 0n) ? '-' : ''}${outInt}${fracStr}%`;
}

